import { fDate } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';

import { ILoanAgreementListItem } from 'src/types/loan-agreement';

import { loanListIdLine } from './loan-agreement-table-row';

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Prints every agreement matching the current tab/filters/search (not just
 * the visible page) in a standalone window, so the dashboard chrome isn't
 * part of the printout. The window must be opened by the caller directly in
 * the click handler (before fetching), or pop-up blockers will stop it.
 */
export function printLoanAgreements(
  printWindow: Window,
  rows: ILoanAgreementListItem[],
  meta: { title: string; filters: string[] }
) {
  const totals = rows.reduce(
    (acc, row) => ({
      capital: acc.capital + row.capitalInCents,
      paid: acc.paid + row.paidInCents,
      remaining: acc.remaining + row.remainingInCents,
    }),
    { capital: 0, paid: 0, remaining: 0 }
  );

  const body = rows
    .map(
      (row, index) => `<tr>
        <td>${index + 1}</td>
        <td><strong>${escapeHtml(row.displayName)}</strong><br><span class="muted">${escapeHtml(
          loanListIdLine(row)
        )}</span></td>
        <td>${row.borrowerType === 'employee' ? 'Employee' : 'Other'}</td>
        <td>${escapeHtml(fDate(row.loanPaidDate))}</td>
        <td class="num">${escapeHtml(fCurrencyRupees(row.capitalInCents))}</td>
        <td>${escapeHtml(fDate(row.settlementDate))}</td>
        <td class="num">${escapeHtml(fCurrencyRupees(row.paidInCents))} (${
          row.progressPercent
        }%)</td>
        <td class="num">${escapeHtml(fCurrencyRupees(row.remainingInCents))}${
          row.overdueAmountInCents > 0 && row.status === 'active'
            ? `<br><span class="overdue">Overdue ${escapeHtml(
                fCurrencyRupees(row.overdueAmountInCents)
              )}</span>`
            : ''
        }</td>
      </tr>`
    )
    .join('');

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(meta.title)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #111827; margin: 24px; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .meta { font-size: 12px; color: #6b7280; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { border-bottom: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; font-size: 11px; text-transform: uppercase; color: #4b5563; }
  .num { text-align: right; white-space: nowrap; }
  .muted { color: #6b7280; }
  .overdue { color: #92400e; font-weight: bold; }
  tfoot td { font-weight: bold; border-top: 2px solid #111827; }
  @media print { body { margin: 0; } }
</style>
</head>
<body>
  <h1>${escapeHtml(meta.title)}</h1>
  <div class="meta">${escapeHtml(
    [
      ...meta.filters,
      `${rows.length} agreement${rows.length === 1 ? '' : 's'}`,
      `Printed ${fDate(new Date())}`,
    ].join(' · ')
  )}</div>
  <table>
    <thead>
      <tr>
        <th>#</th><th>Borrower</th><th>Type</th><th>Loan Paid</th><th class="num">Capital</th>
        <th>Settlement Date</th><th class="num">Paid</th><th class="num">Remaining</th>
      </tr>
    </thead>
    <tbody>${body}</tbody>
    <tfoot>
      <tr>
        <td colspan="4">Total</td>
        <td class="num">${escapeHtml(fCurrencyRupees(totals.capital))}</td>
        <td></td>
        <td class="num">${escapeHtml(fCurrencyRupees(totals.paid))}</td>
        <td class="num">${escapeHtml(fCurrencyRupees(totals.remaining))}</td>
      </tr>
    </tfoot>
  </table>
</body>
</html>`;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}
